/// <amd-module name="Wit/Settings/BoardsExternalIntegration/components/panels/removeRepo/RemoveRepoPicker" />
import { CheckboxVisibility, DetailsListLayoutMode, IColumn, SelectionMode } from "OfficeFabric/DetailsList";
import { Selection } from "OfficeFabric/Selection";
import * as React from "react";
import { localeFormat, localeIgnoreCaseComparer } from "VSS/Core/Util/String";
import { IVssComponentContext, VssComponent } from "VSS/Platform/Layout";
import { FilterBar } from "VSSUI/FilterBar";
import { KeywordFilterBarItem } from "VSSUI/TextFilterBarItem";
import { Filter, FILTER_CHANGE_EVENT, IFilterState } from "VSSUI/Utilities/Filter";
import { VssDetailsList, VssDetailsListRowStyle } from "VSSUI/VssDetailsList";
import { ExternalGitRepo } from "Wit/Clients/Internal/Contracts/ExternalEvents";
import { updateSelectedRepos } from "Wit/Settings/BoardsExternalIntegration/components/common/Utilities";
import { IRemoveRepoPickerProps } from "Wit/Settings/BoardsExternalIntegration/components/panels/removeRepo/RemoveRepoPicker.Props";
import * as Resources from "Wit/Settings/BoardsExternalIntegration/Resources";

export interface IRemoveRepoPickerState {
    selectedRepos: { [repoNodeId: string]: ExternalGitRepo };
    filteredRepos?: ExternalGitRepo[];
}

const keywordsFilterKey = "KeywordsFilter";
const keywordsFilterThrottleWait = 400;

export class RemoveRepoPicker extends VssComponent<IRemoveRepoPickerProps, IRemoveRepoPickerState> {
    private _existingRepos: ExternalGitRepo[];
    private _filter: Filter;
    private _selection: Selection;
    private _filterBarRef = React.createRef<FilterBar>();

    constructor(props: IRemoveRepoPickerProps, context: IVssComponentContext) {
        super(props, context);

        this._existingRepos = (this.props.existingRepos || []).sort((a: ExternalGitRepo, b: ExternalGitRepo) =>
            localeIgnoreCaseComparer(a.name, b.name)
        );

        this._filter = new Filter();

        this._selection = new Selection({
            onSelectionChanged: this._onReposSelectionChanged,
            selectionMode: SelectionMode.multiple
        });

        this.state = {
            selectedRepos: {}
        };
    }

    public render() {
        const repos = this._getItems();
        const columns = this._getColumns();

        return (
            <div className="remove-repo-picker">
                <FilterBar filter={this._filter} ref={this._filterBarRef}>
                    <KeywordFilterBarItem
                        filterItemKey={keywordsFilterKey}
                        placeholder={Resources.RepositoryPickerKeywordsFilter_Placeholder}
                        throttleWait={keywordsFilterThrottleWait}
                    />
                </FilterBar>
                {this._renderInfomationText()}
                <div className="existing-repo-list">
                    <VssDetailsList
                        items={repos}
                        columns={columns}
                        isHeaderVisible={false}
                        selection={this._selection}
                        checkboxVisibility={CheckboxVisibility.always}
                        layoutMode={DetailsListLayoutMode.justified}
                        selectionPreservedOnEmptyClick={true}
                        rowStyle={VssDetailsListRowStyle.oneLine}
                    />
                </div>
            </div>
        );
    }

    public componentDidMount(): void {
        this._filter.subscribe(this._onFilterChanged, FILTER_CHANGE_EVENT);
        requestAnimationFrame(() => {
            if (this._filterBarRef.current) {
                this._filterBarRef.current.focus();
            }
        });
    }

    public componentWillUnmount(): void {
        this._filter.unsubscribe(this._onFilterChanged, FILTER_CHANGE_EVENT);
    }

    private _renderInfomationText(): JSX.Element {
        const numberOfReposInView = this._getItems().length;
        const numberOfReposTotal = this._existingRepos.length;
        const numberOfReposSelected = Object.keys(this.state.selectedRepos).length;
        const isFilterApplied = !!this.state.filteredRepos;

        let infoText: string;
        if (isFilterApplied) {
            infoText = localeFormat(Resources.RepositoryPickerViewStatus_Filtered, numberOfReposInView, numberOfReposTotal, numberOfReposSelected);
        } else {
            infoText = localeFormat(Resources.RepositoryPickerViewStatus_NotFiltered, numberOfReposInView, numberOfReposSelected);
        }

        return <div className="info-text">{infoText}</div>;
    }

    private _getItems(): ExternalGitRepo[] {
        const { filteredRepos } = this.state;
        return filteredRepos ? filteredRepos : this._existingRepos;
    }

    private _getColumns(): IColumn[] {
        return [
            {
                key: "repo-name",
                name: "",
                fieldName: "",
                minWidth: 100,
                className: "repo-name-column",
                onRender: (repo: ExternalGitRepo) => <span className="repository-name">{repo.name}</span>
            }
        ];
    }

    private _onFilterChanged = (newValue: IFilterState, action: string) => {
        const keywordsFilterState = newValue[keywordsFilterKey];
        if (keywordsFilterState) {
            const keywordsText = keywordsFilterState.value;
            const filteredRepos = this._filterRepos(keywordsText);
            this.setState(
                {
                    filteredRepos: filteredRepos
                },
                this._updateSelectionForFilteredList
            );
        } else {
            this.setState(
                {
                    filteredRepos: undefined
                },
                this._updateSelectionForFilteredList
            );
        }
    };

    private _filterRepos(keywordsText?: string): ExternalGitRepo[] | undefined {
        if (keywordsText && this._existingRepos && this._existingRepos.length > 0) {
            const searchForString = keywordsText.trim().toLowerCase();
            return this._existingRepos.filter(repo => repo.name.toLowerCase().indexOf(searchForString) !== -1);
        }
        return undefined;
    }

    /**
     * Updates selection each time the filter is applied since the selection will be reset when details list items changed.
     */
    private _updateSelectionForFilteredList = (): void => {
        const listedRepos = this._selection.getItems() as ExternalGitRepo[];
        const { selectedRepos } = this.state;

        for (let index = 0; index < listedRepos.length; index++) {
            const repo = listedRepos[index];
            this._selection.setIndexSelected(index, !!selectedRepos[repo.id], false);
        }
    };

    private _onReposSelectionChanged = () => {
        const listedRepos = this._selection.getItems() as ExternalGitRepo[];
        const selectedRepos = { ...this.state.selectedRepos };

        updateSelectedRepos(listedRepos, selectedRepos, this._selection);

        this.setState({
            selectedRepos: selectedRepos
        });

        const { onRemoveReposSelectionChanged } = this.props;
        onRemoveReposSelectionChanged && onRemoveReposSelectionChanged(selectedRepos);
    };
}
